/**
 * @architecture-header 1
 * @layer module
 * @domain classrooms
 * @responsibility Render independent desktop and mobile roster-management surfaces over the authoritative member query and removal mutation.
 * @state-owner TanStack Query member cache and local confirmation state
 * @allowed-imports React; active-context provider; classrooms query/mutation APIs; shared loading/error primitives; member model types
 * @forbidden direct API clients; client-side authorization decisions; role toggles
 * @public-api ClassroomMemberManagement
 * @side-effects authenticated member-removal command and roster cache invalidation
 * @tests ClassroomMemberManagement.test.tsx and frontend/apps/web/e2e/classroom-members.spec.ts
 */

import { useRef, useState } from 'react';
import { useActiveContextId } from '../../../app/providers/ActiveRoleProvider';
import { VispoLoading } from '../../../shared/components/WorkspaceState';
import { hasHttpStatus } from '../../../shared/lib/api-error';
import { useIsMobile } from '../../../shared/model/use-workspace';
import { useDeleteClassroomMember } from '../api/classrooms.mutations';
import { useClassStudents } from '../api/classrooms.queries';
import type { ClassMemberSummary, ClassroomSummary } from '../model/types';
import './classroom-member-management.css';

function idempotencyKey() {
  return `agent-b-cl-f07-remove-${crypto.randomUUID?.() ?? Date.now()}`;
}

function MemberRow({
  member,
  mobile = false,
  onRemove,
}: {
  member: ClassMemberSummary;
  mobile?: boolean;
  onRemove: (member: ClassMemberSummary) => void;
}) {
  const roleLabel =
    member.role === 'owner' ? '班級擁有者' : member.role === 'teacher' ? '共同老師' : '學生';
  return (
    <li className="class-member-management__row">
      <span className="class-member-management__avatar" aria-hidden="true">
        {member.name.slice(0, 1)}
      </span>
      <span className="class-member-management__identity">
        <strong>{member.name}</strong>
        <small>{roleLabel}</small>
      </span>
      {member.role === 'owner' ? (
        <span className="class-member-management__protected">不可移除</span>
      ) : (
        <button
          aria-label={`移除 ${member.name}`}
          className="bt sm class-member-management__remove"
          onClick={() => onRemove(member)}
          type="button"
        >
          {mobile ? '移除' : '移除成員'}
        </button>
      )}
    </li>
  );
}

function MemberManagementState({
  members,
  onRemove,
  mobile = false,
}: {
  members: ReturnType<typeof useClassStudents>;
  onRemove: (member: ClassMemberSummary) => void;
  mobile?: boolean;
}) {
  if (members.isLoading || members.isFetching) {
    return <VispoLoading label="正在載入班級成員" />;
  }
  if (members.isError) {
    const denied = hasHttpStatus(members.error, 403);
    return (
      <p className="class-member-management__message" role="alert">
        {denied ? '你沒有權限查看這個班級的成員。' : '成員名單暫時無法載入，請稍後再試。'}
      </p>
    );
  }
  const activeMembers = (members.data ?? []).filter((member) => member.status === 'active');
  if (!activeMembers.length) {
    return <p className="class-member-management__message">這個班級目前沒有正式成員。</p>;
  }
  return (
    <ul className="class-member-management__list">
      {activeMembers.map((member) => (
        <MemberRow key={member.id} member={member} mobile={mobile} onRemove={onRemove} />
      ))}
    </ul>
  );
}

function MemberRemovalDialog({
  pending,
  removal,
  onCancel,
  onConfirm,
}: {
  pending: ClassMemberSummary;
  removal: ReturnType<typeof useDeleteClassroomMember>;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div aria-label="確認移除成員" className="class-member-management__confirm" role="dialog">
      <p>
        確定要移除 <strong>{pending.name}</strong> 嗎？移除後將無法再進入這個班級。
      </p>
      {removal.isError ? (
        <p className="class-member-management__error" role="alert">
          {hasHttpStatus(removal.error, 409)
            ? '成員狀態已變更，請重新整理名單。'
            : '移除失敗，請稍後再試。'}
        </p>
      ) : null}
      <div className="class-member-management__confirm-actions">
        <button className="bt sm" disabled={removal.isPending} onClick={onCancel} type="button">
          取消
        </button>
        <button
          className="bt sm pri"
          disabled={removal.isPending}
          onClick={onConfirm}
          type="button"
        >
          {removal.isPending ? '正在移除…' : '確認移除'}
        </button>
      </div>
    </div>
  );
}

function DesktopMemberSurface({
  members,
  pending,
  removal,
  onRemove,
  onCancel,
  onConfirm,
}: {
  members: ReturnType<typeof useClassStudents>;
  pending: ClassMemberSummary | null;
  removal: ReturnType<typeof useDeleteClassroomMember>;
  onRemove: (member: ClassMemberSummary) => void;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="class-member-management__surface class-member-management__surface--desktop">
      <div className="class-member-management__heading">
        <div>
          <p className="eyebrow">班級名單</p>
          <h3>正式成員</h3>
        </div>
        <span>{members.data?.filter((member) => member.status === 'active').length ?? 0} 人</span>
      </div>
      <MemberManagementState members={members} onRemove={onRemove} />
      {pending ? (
        <MemberRemovalDialog
          onCancel={onCancel}
          onConfirm={onConfirm}
          pending={pending}
          removal={removal}
        />
      ) : null}
    </div>
  );
}

function MobileMemberSurface({
  members,
  pending,
  removal,
  onRemove,
  onCancel,
  onConfirm,
}: {
  members: ReturnType<typeof useClassStudents>;
  pending: ClassMemberSummary | null;
  removal: ReturnType<typeof useDeleteClassroomMember>;
  onRemove: (member: ClassMemberSummary) => void;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="class-member-management__surface class-member-management__surface--mobile">
      <div className="class-member-management__heading">
        <div>
          <p className="eyebrow">班級名單</p>
          <h3>班級成員</h3>
        </div>
        <span>{members.data?.filter((member) => member.status === 'active').length ?? 0} 人</span>
      </div>
      <MemberManagementState members={members} mobile onRemove={onRemove} />
      {pending ? (
        <MemberRemovalDialog
          onCancel={onCancel}
          onConfirm={onConfirm}
          pending={pending}
          removal={removal}
        />
      ) : null}
    </div>
  );
}

export function ClassroomMemberManagement({
  classroom,
}: {
  classroom: Pick<ClassroomSummary, 'id' | 'students'>;
}) {
  const contextId = useActiveContextId();
  const members = useClassStudents(contextId, classroom);
  const removal = useDeleteClassroomMember();
  const mobile = useIsMobile();
  const [pending, setPending] = useState<ClassMemberSummary | null>(null);
  const removalCommand = useRef<{ memberUserId: string; idempotencyKey: string } | null>(null);

  const confirmRemoval = () => {
    if (!pending || !removalCommand.current) return;
    removal.mutate(
      {
        activeContextId: contextId,
        classroomId: classroom.id,
        memberUserId: removalCommand.current.memberUserId,
        idempotencyKey: removalCommand.current.idempotencyKey,
      },
      {
        onSuccess: () => {
          removalCommand.current = null;
          setPending(null);
        },
      },
    );
  };
  const beginRemoval = (member: ClassMemberSummary) => {
    removalCommand.current = {
      memberUserId: member.userId,
      idempotencyKey: idempotencyKey(),
    };
    setPending(member);
  };
  const cancelRemoval = () => {
    removalCommand.current = null;
    setPending(null);
  };
  const surfaceProps = {
    members,
    onCancel: cancelRemoval,
    onConfirm: confirmRemoval,
    onRemove: beginRemoval,
    pending,
    removal,
  };
  return (
    <section aria-label="班級成員管理" className="class-member-management">
      {mobile ? (
        <MobileMemberSurface {...surfaceProps} />
      ) : (
        <DesktopMemberSurface {...surfaceProps} />
      )}
    </section>
  );
}
